'use client';

// components
import Image from 'next/image';
import VisibleLoading from '@/components/utilities/VisibleLoading';
import { useEffect, useRef } from 'react';

// utilities
import { getImagePath } from '@/utils/images';

const ClientLineBlocks = ({ clients }) => {
  const containerRef = useRef(null);
  const leftColumnRef = useRef(null);
  const rightColumnRef = useRef(null);
  const rafRef = useRef(null);
  const isScrollingRef = useRef(false);

  useEffect(() => {
    const updateTransform = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Kontrola, zda je kontejner ve viewportu
      const isInViewport = rect.top < windowHeight && rect.bottom > 0;
      const isMobile = window.innerWidth < 1280; // breakpoint pro xl

      if (!isInViewport) {
        isScrollingRef.current = false;
        return;
      }

      // Vypočítáme offset relativně k pozici kontejneru ve viewportu
      const elementTop = rect.top;
      const elementHeight = rect.height;

      // Progress od 0 (začátek vstupu do viewportu) do 1 (konec výstupu z viewportu)
      const progress = (windowHeight - elementTop) / (windowHeight + elementHeight);
      const speed = 200; // Maximální posun v pixelech
      const offset = (progress - 0.5) * speed * 2; // Centrujeme kolem 0

      if (leftColumnRef.current) {
        leftColumnRef.current.style.transform = isMobile ? `translateX(${offset + 250}px)` : `translateY(${-offset}px)`;
      }
      if (rightColumnRef.current) {
        rightColumnRef.current.style.transform = isMobile
          ? `translateX(${-offset - 250}px)`
          : `translateY(${offset}px)`;
      }

      isScrollingRef.current = false;
    };

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      isScrollingRef.current = true;

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(updateTransform);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateTransform(); // Initial calculation

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <div className="relative h-70 xl:h-full xl:w-auto">
      <div
        ref={containerRef}
        className="absolute -left-[5vw] flex h-[calc(100%+28px*4*2)] w-[100vw] justify-center gap-3 overflow-hidden xl:-top-28 xl:left-0 xl:w-full"
      >
        <div
          ref={leftColumnRef}
          className="flex flex-row justify-start gap-2 transition-transform duration-0 xl:-mt-10 xl:flex-col xl:gap-3"
        >
          {(clients || [])
            .filter((_, index) => index % 2 === 0)
            .map((client) => (
              <div
                key={client.id}
                className="flex h-30 w-30 shrink-0 items-center justify-center rounded-xl border border-[0.75] border-white/20 bg-gray-200 bg-white/4 backdrop-blur-sm xl:h-50 xl:w-50"
              >
                {client.image && (
                  <Image
                    className="max-h-[50px] max-w-[70%] object-contain"
                    src={getImagePath(client.image)}
                    width={client.image.width}
                    height={client.image.height}
                    sizes="(min-width: 1280px) 200px, 120px"
                    alt={client.name}
                  />
                )}
              </div>
            ))}
        </div>

        <div
          ref={rightColumnRef}
          className="mt-32 flex justify-end gap-2 transition-transform duration-0 xl:mt-0 xl:flex-col xl:gap-3"
        >
          {(clients || [])
            .filter((_, index) => index % 2 === 1)
            .map((client) => (
              <div
                key={client.id}
                className="flex h-30 w-30 shrink-0 items-center justify-center rounded-xl border border-[0.75] border-white/20 bg-gray-200 bg-white/4 backdrop-blur-sm xl:h-50 xl:w-50"
              >
                {client.image && (
                  <Image
                    className="max-h-[55px] max-w-[70%] object-contain"
                    src={getImagePath(client.image)}
                    width={client.image.width}
                    height={client.image.height}
                    sizes="(min-width: 1280px) 200px, 120px"
                    alt={client.name}
                  />
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};
export default ClientLineBlocks;
